import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "@/context/QuoteContext";
import CatalogModal from "@/components/CatalogModal";

export default function EndCTA() {
  const navigate = useNavigate();
  const { openQuote } = useQuote();
  const [catalogOpen, setCatalogOpen] = useState(false);

  const promises = [
    { k: "Made", v: "To Order" },
    { k: "Delivered", v: "Nationwide" },
    { k: "European", v: "Craftsmanship" },
  ];

  return (
    <section
      className="relative w-full min-h-[100svh] overflow-hidden bg-brand-charcoal text-brand-bone"
      data-testid="end-cta-section"
    >
      {/* Full-bleed background */}
      <motion.img
        src="/end-hero.png"
        alt="Walnut interior door — Atelier"
        initial={{ scale: 1.08 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Vignette + brand gradient — darken bottom + left where text sits */}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/95 via-brand-charcoal/55 to-brand-charcoal/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/25 to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(11,22,20,0.35)_75%)]" />

      {/* Content */}
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-16 md:pb-20 flex flex-col min-h-[100svh]">
        <div className="flex items-center gap-3 mb-8">
          <span className="text-[0.68rem] tracking-[0.32em] uppercase text-brand-gold font-medium">
            The Final Threshold
          </span>
          <span className="h-px w-16 bg-brand-gold/40" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl font-light leading-[1.02] tracking-[-0.02em] text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] text-brand-bone"
        >
          Every entrance,
          <span className="block italic font-serif text-brand-gold">a signature.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-8 max-w-xl text-brand-bone/80 leading-relaxed font-light md:text-lg"
        >
          Configure your Atelier door in minutes, or reserve a private moment with our concierge.
          Either way, you leave with an entrance no other home will have.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.28 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={() => navigate("/collection/exterior")}
            className="btn-primary"
            data-testid="end-cta-configure"
          >
            Configure Doors
            <span aria-hidden>→</span>
          </button>
          <button
            onClick={() => setCatalogOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-bone text-brand-green text-[0.72rem] tracking-[0.24em] uppercase rounded-full hover:bg-brand-gold transition font-medium"
            data-testid="end-cta-download-catalog"
          >
            Download Catalog
            <span aria-hidden>↓</span>
          </button>
          <a
            href="#concierge"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("[data-testid='chat-toggle']")?.click();
            }}
            className="inline-flex items-center gap-2 px-6 py-3 border border-brand-bone/40 text-brand-bone text-[0.72rem] tracking-[0.24em] uppercase rounded-full hover:border-brand-gold hover:text-brand-gold transition"
            data-testid="end-cta-concierge"
          >
            Speak With Concierge
          </a>
        </motion.div>

        <div className="flex-1" />

        {/* Trust ribbon */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5 max-w-4xl border-t border-brand-gold/25 pt-6"
          data-testid="end-cta-promises"
        >
          {promises.map((p) => (
            <div key={p.v}>
              <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-gold/85">{p.k}</div>
              <div className="mt-1.5 text-brand-bone text-lg md:text-xl font-light tracking-[-0.01em]">
                {p.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
      <CatalogModal open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </section>
  );
}
